import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowDownToLine,
  Bot,
  BookOpen,
  Boxes,
  CheckCircle2,
  ChevronRight,
  Cpu,
  GitBranch,
  HeartPulse,
  LayoutDashboard,
  MessageSquareText,
} from 'lucide-react';
import PageShell from '../../components/PageShell';
import StatsCard from '../../components/StatsCard';
import { useAuthStore } from '../../store/authStore';
import type { AppInstance } from '../apps/types';
import { isAppActive } from '../apps/appInstanceStatus';
import HealthBanner from './HealthBanner';
import PlatformHealthCard from './components/PlatformHealthCard';
import {
  getTelemetryMsgsPerMin,
  useAgenticNotifications,
  useAppInstances,
  useFleetSummary,
  useMailboxUnreadMap,
  useRecentAgentRuns,
  useTelemetryAgg,
} from './hooks';

type ActivityTone = 'info' | 'success' | 'warning' | 'error';

type ActivityItem = {
  id: string;
  ts: string;
  title: string;
  detail: string;
  tone: ActivityTone;
  href: string;
};

const PANEL_CLASS = 'rounded-2xl bg-white p-5';
const PANEL_STYLE = { boxShadow: 'var(--shadow-card)' } as const;

function toMs(value: unknown): number {
  if (!value) return 0;
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isFinite(date.getTime()) ? date.getTime() : 0;
}

function formatRelativeTime(value: string) {
  const diffMs = Date.now() - toMs(value);
  if (diffMs <= 0) return 'just now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatMetricCount(value?: number) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '0';
  if (value === 0) return '0';
  if (value >= 10) return Math.round(value).toLocaleString();
  return value.toFixed(1);
}

function getStatusLabel(status?: string | null) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'active' || normalized === 'ready') return 'Active';
  if (normalized === 'provisioning') return 'Provisioning';
  if (normalized === 'failed') return 'Needs attention';
  if (normalized === 'deleted') return 'Deleted';
  return normalized ? normalized.replace(/_/g, ' ') : 'Unknown';
}

function getStatusDotClass(status?: string | null) {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'active' || normalized === 'ready') return 'bg-emerald-500';
  if (normalized === 'provisioning') return 'bg-amber-500';
  if (normalized === 'failed') return 'bg-red-500';
  if (normalized === 'deleted') return 'bg-slate-300';
  return 'bg-slate-400';
}

function getInitials(name: string) {
  return String(name || 'App')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
}

function getToneClasses(tone: ActivityTone) {
  if (tone === 'success') return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (tone === 'warning') return 'bg-amber-50 text-amber-700 border border-amber-200';
  if (tone === 'error') return 'bg-red-50 text-red-700 border border-red-200';
  return 'bg-blue-50 text-blue-700 border border-blue-200';
}

function getActivityIcon(tone: ActivityTone) {
  if (tone === 'success') return CheckCircle2;
  if (tone === 'warning') return AlertTriangle;
  if (tone === 'error') return AlertTriangle;
  return Activity;
}

function buildExecutionActivity(run: any): ActivityItem {
  const ts = String(run?.executed_at || run?.created_at || new Date().toISOString());
  const agentName = String(run?.agent_name || run?.agent_id || 'Agent run');
  const rawStatus = String(run?.status || run?.result || '').toLowerCase();
  const requestId = run?.request_id ? `request ${run.request_id}` : null;
  const appRef = run?.app_id || run?.input?.app_id || run?.input?.app_name || null;
  const errorText = run?.error ? String(run.error).slice(0, 120) : null;

  const tone: ActivityTone = rawStatus.includes('fail') || rawStatus.includes('error')
    ? 'error'
    : rawStatus.includes('warn')
      ? 'warning'
      : rawStatus.includes('success') || rawStatus.includes('complete')
        ? 'success'
        : 'info';

  const title = rawStatus
    ? `${agentName} ${rawStatus.replace(/_/g, ' ')}`
    : `${agentName} executed`;

  const detail = errorText || [requestId, appRef ? `app ${appRef}` : null].filter(Boolean).join(' · ') || 'Recent agent execution';
  const href = run?.request_id
    ? `/agents/observe?request_id=${encodeURIComponent(String(run.request_id))}`
    : '/agents/observe';

  return { id: `execution-${run?._id || run?.execution_id || ts}`, ts, title, detail, tone, href };
}

function ActivityPanel({ items, isLoading }: { items: ActivityItem[]; isLoading: boolean }) {
  return (
    <section className={PANEL_CLASS} style={PANEL_STYLE}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Recent Activity</h2>
          <p className="text-xs text-slate-500">Agent operations and recent agent execution merged into one timeline.</p>
        </div>
        <Link to="/agents/observe" className="text-xs font-medium text-blue-600 hover:text-blue-700">
          View all
        </Link>
      </div>

      {isLoading && (
        <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          Loading recent activity...
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
          No agentic or execution activity has been captured in the recent window.
        </div>
      )}

      {!isLoading && items.length > 0 && (
        <div className="space-y-3">
          {items.map((item) => {
            const Icon = getActivityIcon(item.tone);
            return (
              <Link
                key={item.id}
                to={item.href}
                className="flex items-start gap-3 rounded-xl border border-slate-100 px-3 py-3 transition-colors hover:bg-slate-50"
              >
                <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${getToneClasses(item.tone)}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-900">{item.title}</p>
                      <p className="mt-0.5 text-xs text-slate-600">{item.detail}</p>
                    </div>
                    <span className="shrink-0 text-[11px] text-slate-400">{formatRelativeTime(item.ts)}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

function AppInstancesPanel({
  apps,
  unreadMap,
  isLoading,
}: {
  apps: AppInstance[];
  unreadMap: Map<string, number>;
  isLoading: boolean;
}) {
  return (
    <section className={PANEL_CLASS} style={PANEL_STYLE}>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">App Instances</h2>
          <p className="text-xs text-slate-500">Click any app to open it.</p>
        </div>
        <Link to="/apps" className="text-xs font-medium text-blue-600 hover:text-blue-700">
          View all
        </Link>
      </div>

      {isLoading && (
        <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          Loading app instances...
        </div>
      )}

      {!isLoading && apps.length === 0 && (
        <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
          No app instances found for this tenant yet.
        </div>
      )}

      {!isLoading && apps.length > 0 && (
        <div className="space-y-3">
          {apps.map((app) => {
            const unread = Number(unreadMap.get(String(app.app_id)) || 0);
            return (
              <Link
                key={app.app_id}
                to={`/apps/${encodeURIComponent(String(app.app_id))}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 px-3 py-3 transition-colors hover:bg-slate-50"
              >
                <div className="flex min-w-0 items-center gap-3">
                  {app.logo_url ? (
                    <img
                      src={String(app.logo_url)}
                      alt={app.name}
                      className="h-10 w-10 rounded-xl border border-slate-200 object-cover"
                    />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-sm font-semibold text-slate-700">
                      {getInitials(app.name)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-slate-900">{app.name}</p>
                      {unread > 0 && (
                        <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 border border-amber-200">
                          {unread}
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                      <span className={`h-2 w-2 rounded-full ${getStatusDotClass(app.status)}`} />
                      <span>{getStatusLabel(app.status)}</span>
                    </div>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

function QuickNavCard({
  title,
  description,
  to,
  icon: Icon,
  color,
}: {
  title: string;
  description: string;
  to: string;
  icon: typeof HeartPulse;
  color: string;
}) {
  return (
    <Link
      to={to}
      className="rounded-2xl border border-slate-200 bg-white p-4 transition-all hover:-translate-y-0.5 hover:bg-slate-50"
      style={PANEL_STYLE}
    >
      <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-xl ${color}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div className="space-y-1">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <p className="text-xs leading-5 text-slate-500">{description}</p>
      </div>
    </Link>
  );
}

export default function NewOverviewPage() {
  const { t } = useTranslation();
  const { token, selectedTenant } = useAuthStore();
  const enabled = Boolean(token && selectedTenant);

  const appInstancesQ = useAppInstances(enabled);
  const agentRunsQ = useRecentAgentRuns(enabled, 50);
  const fleetSummaryQ = useFleetSummary(enabled);
  const telemetryAggQ = useTelemetryAgg(enabled);
  const agenticNotificationsQ = useAgenticNotifications(enabled);

  const apps = Array.isArray(appInstancesQ.data) ? appInstancesQ.data : [];
  const unreadSummaryQ = useMailboxUnreadMap(apps, enabled);

  const recentRuns = Array.isArray(agentRunsQ.data) ? agentRunsQ.data : [];
  const telemetryMsgsPerMin = getTelemetryMsgsPerMin(telemetryAggQ.data);
  const fleetUnavailable = fleetSummaryQ.isError;
  const totalDevices = fleetSummaryQ.data?.total_devices;
  const emittingDevices = Array.isArray(fleetSummaryQ.data?.models)
    ? fleetSummaryQ.data.models.reduce((sum, model) => sum + Number(model?.emitting || 0), 0)
    : 0;

  const now = Date.now();
  const oneDayAgo = now - 24 * 60 * 60 * 1000;
  const agentRuns24h = recentRuns.filter((run) => toMs(run?.executed_at || run?.created_at) >= oneDayAgo);
  const failedRuns24h = agentRuns24h.filter((run) => {
    const status = String(run?.status || run?.result || '').toLowerCase();
    return status.includes('fail') || status.includes('error');
  });

  const activeApps = apps.filter(isAppActive).length;
  const provisioningApps = apps.filter((app) => String(app?.status || '').toLowerCase() === 'provisioning').length;

  const activityItems = useMemo(() => {
    const notificationItems = (agenticNotificationsQ.data || []).map((item) => ({
      id: `agentic-${item.id}`,
      ts: item.ts,
      title: item.title,
      detail: item.detail,
      tone: item.severity,
      href: '/agents/observe',
    })) as ActivityItem[];

    const executionItems = recentRuns.map((run) => buildExecutionActivity(run));

    return [...notificationItems, ...executionItems]
      .sort((a, b) => toMs(b.ts) - toMs(a.ts))
      .slice(0, 8);
  }, [agenticNotificationsQ.data, recentRuns]);

  const appCards = useMemo(() => {
    return [...apps]
      .sort((a, b) => {
        const statusScore = (app: AppInstance) => {
          const status = String(app?.status || '').toLowerCase();
          if (status === 'active' || status === 'ready') return 0;
          if (status === 'provisioning') return 1;
          if (status === 'failed') return 2;
          return 3;
        };
        const diff = statusScore(a) - statusScore(b);
        if (diff !== 0) return diff;
        return String(a?.name || '').localeCompare(String(b?.name || ''));
      })
      .slice(0, 5);
  }, [apps]);

  if (!enabled) {
    return (
      <PageShell
        title={t('overview.platformPulseTitle', 'Platform Pulse')}
        subtitle={t('overview.authRequired', 'Select a tenant and authenticate to view the control plane status.')}
        icon={LayoutDashboard}
        iconColor="bg-blue-600"
      >
        <div />
      </PageShell>
    );
  }

  return (
    <PageShell
      title={t('overview.platformPulseTitle', 'Platform Pulse')}
      subtitle={t('overview.platformPulseSubtitle', 'Live platform health, app readiness, and tenant activity in one place.')}
      icon={LayoutDashboard}
      iconColor="bg-blue-600"
    >
      <HealthBanner enabled={enabled} />

      <PlatformHealthCard enabled={enabled} />

      <section>
        <h2 className="mb-1 text-base font-semibold text-slate-900">Live KPIs</h2>
        <p className="mb-3 text-xs text-slate-500">Current tenant activity across apps, agents, devices, and telemetry.</p>
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-4 sm:grid-cols-2">
          <StatsCard
            title="App Instances"
            count={apps.length}
            subtitle="Apps in this view"
            linkTo="/apps"
            icon={Boxes}
            color="bg-emerald-600"
            trend={
              apps.length === 0
                ? 'No apps yet'
                : provisioningApps > 0
                  ? `${provisioningApps} provisioning`
                  : `${activeApps} active`
            }
            trendTone={
              apps.length === 0
                ? 'neutral'
                : provisioningApps > 0
                  ? 'warning'
                  : 'success'
            }
          />
          <StatsCard
            title="Agent Runs (24h)"
            count={agentRuns24h.length}
            subtitle="Recent execution volume"
            linkTo="/agents/observe"
            icon={Bot}
            color="bg-orange-500"
            trend={
              agentRuns24h.length === 0
                ? 'No recent runs'
                : failedRuns24h.length > 0
                  ? `${failedRuns24h.length} need review`
                  : 'Runs look healthy'
            }
            trendTone={
              agentRuns24h.length === 0
                ? 'neutral'
                : failedRuns24h.length > 0
                  ? 'warning'
                  : 'success'
            }
          />
          <StatsCard
            title="IoT Devices"
            count={fleetUnavailable ? '—' : Number(totalDevices ?? 0)}
            subtitle="fleet-summary (same store as IoT Shack)"
            linkTo="/iotshack"
            icon={Cpu}
            color="bg-cyan-600"
            trend={
              fleetUnavailable
                ? 'Fleet summary unavailable — not zero'
                : totalDevices == null
                  ? 'Fleet summary not loaded'
                  : totalDevices === 0
                    ? 'fleet-summary returned 0'
                    : `${emittingDevices} emitting now`
            }
            trendTone={fleetUnavailable || totalDevices == null ? 'warning' : totalDevices === 0 ? 'neutral' : emittingDevices > 0 ? 'success' : 'warning'}
          />
          <StatsCard
            title="Msg/min"
            count={formatMetricCount(telemetryMsgsPerMin)}
            subtitle="telemetry/agg 1h (same source as System Health)"
            linkTo="/system"
            icon={MessageSquareText}
            color="bg-blue-600"
            trend={
              telemetryAggQ.isError
                ? 'Telemetry agg unavailable'
                : typeof telemetryMsgsPerMin === 'number' && telemetryMsgsPerMin > 0
                  ? 'Ingestion active'
                  : 'No msgs/min in this agg payload'
            }
            trendTone={
              telemetryAggQ.isError
                ? 'warning'
                : typeof telemetryMsgsPerMin === 'number' && telemetryMsgsPerMin > 0
                  ? 'info'
                  : 'neutral'
            }
          />
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <ActivityPanel
          items={activityItems}
          isLoading={agenticNotificationsQ.isLoading || agentRunsQ.isLoading}
        />
        <AppInstancesPanel
          apps={appCards}
          unreadMap={unreadSummaryQ.unreadMap}
          isLoading={appInstancesQ.isLoading || unreadSummaryQ.isLoading}
        />
      </section>

      <section>
        <h2 className="mb-1 text-base font-semibold text-slate-900">Quick Navigate</h2>
        <p className="mb-3 text-xs text-slate-500">Jump straight into the most-used operator surfaces.</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <QuickNavCard
            title="System Health"
            description="Inspect service probes, telemetry freshness, and platform incidents."
            to="/system"
            icon={HeartPulse}
            color="bg-red-500"
          />
          <QuickNavCard
            title="App Instances"
            description="Open tenant apps, validate managers, and review provisioning state."
            to="/apps"
            icon={Boxes}
            color="bg-emerald-600"
          />
          <QuickNavCard
            title="API Ingest"
            description="Watch inbound connector jobs and ingestion pipelines."
            to="/api-ingest"
            icon={ArrowDownToLine}
            color="bg-indigo-600"
          />
          <QuickNavCard
            title="Agent Manager"
            description="Review agent catalogs, workflows, and operator tooling."
            to="/agents"
            icon={Bot}
            color="bg-orange-500"
          />
          <QuickNavCard
            title="Data Flow Manager"
            description="Trace system flows and reduce pipeline bottlenecks quickly."
            to="/dataflow"
            icon={GitBranch}
            color="bg-teal-600"
          />
          <QuickNavCard
            title="Contextualizer"
            description="Manage standards, templates, and registry-backed operations."
            to="/contextualizer"
            icon={BookOpen}
            color="bg-violet-600"
          />
        </div>
      </section>
    </PageShell>
  );
}
